import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import {
  getIcon,
  formatIntegrationName,
  getEditableIntegrationName,
  toIntegrationNameKey,
} from "../../constants/constants";
import { Icons } from "../../constants/icon-library";
import { getIntegration } from "../../features/meeting-integration/meetingIntegrationSlice";
import {
  fetchIntegrationSettingById,
  upsertIntegrationSetting,
  deleteIntegrationSetting,
} from "../../features/integration-setting/integrationSettingSlice";
import LoadingComponent from "../../components/common/LoadingComponent";
import { toast } from "react-toastify";
import { INTEGRATION_ENUM } from "../../constants/constants";
import { confirmationModal } from "../../constants/confirmation.constants";

function IntegrationDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [isEditingAccountSID, setIsEditingAccountSID] = useState(false);
  const [isEditingAuthToken, setIsEditingAuthToken] = useState(false);
  const { selectedIntegration, loading: integrationLoading } = useSelector(
    (state) => state.meetingIntegrations,
  );
  const { setting, loading: settingLoading } = useSelector(
    (state) => state.integrationSetting,
  );
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState("");

  useEffect(() => {
    if (!id) return;

    dispatch(
      fetchIntegrationSettingById({
        settingUuid: id,
      }),
    );
  }, [id, dispatch]);

  const integrationId = useMemo(() => {
    if (!setting?.integrationId) return null;

    return typeof setting.integrationId === "object"
      ? setting.integrationId?.uuid
      : setting.integrationId;
  }, [setting]);

  useEffect(() => {
    if (
      integrationId &&
      (!selectedIntegration || selectedIntegration?.uuid !== integrationId)
    ) {
      dispatch(getIntegration(integrationId));
    }
  }, [integrationId, selectedIntegration, dispatch]);

  const integrationName = selectedIntegration?.name?.toLowerCase();
  const iconSrc = useMemo(() => getIcon(integrationName), [integrationName]);

  const fetchUpdatedSetting = useCallback(async () => {
    if (!setting?.uuid) return;

    await dispatch(
      fetchIntegrationSettingById({
        settingUuid: setting.uuid,
      }),
    ).unwrap();
  }, [dispatch, setting?.uuid]);

  const handleDelete = useCallback(async () => {
    try {
   
   const result = await confirmationModal({
      title: "Delete Integration?",
      text: "Are you sure you want to delete this integration? All associated settings and conversations will also be permanently deleted.",
      confirmText: "Yes, Delete",
      cancelText: "Cancel",
      isDanger: true,
    });

    if (!result.isConfirmed) return;

    await dispatch(
      deleteIntegrationSetting({
        uuid: setting?.uuid,
      }),
    ).unwrap();

    toast.success("Integration deleted successfully");
    navigate("/integrations");
    } catch (error) {
      console.error(error);
    }
  }, [dispatch, setting?.uuid, navigate]);

  const formik = useFormik({
    initialValues: {
      accountSID: "",
      authToken: "",
    },
    enableReinitialize: true,
    validate: (values) => {
      const errors = {};
      if (!values.accountSID?.trim()) {
        errors.accountSID = "Account SID is required";
      }
      if (
        integrationName === INTEGRATION_ENUM.TWILIO &&
        !values.authToken?.trim()
      ) {
        errors.authToken = "Auth Token is required";
      }
      return errors;
    },
  });

  const handleConfiguration = async (setting) => {
    navigate(`/integrations/configuration/${setting?.uuid}`);
  };
  const isLoading = integrationLoading || settingLoading;

  const handleAccountSIDSave = async () => {
    if (!formik.values.accountSID?.trim()) {
      await Promise.all([
        formik.setFieldTouched("accountSID", true, false),
        formik.setFieldError("accountSID", "Account SID is required"),
      ]);
      return;
    }

    formik.setFieldError("accountSID", undefined);

    try {
      await dispatch(
        upsertIntegrationSetting({
          uuid: setting?.uuid,
          integrationId,
          connectionIds: { id1: formik.values.accountSID },
          isInstallationCompleted: true,
        }),
      ).unwrap();

      setIsEditingAccountSID(false);
      await fetchUpdatedSetting();

      toast.success("Account SID updated successfully");
    } catch (error) {
      console.error(error);
      toast.error(error?.message || "Failed to update Account SID");
    }
  };

  const handleAuthTokenSave = async () => {
    if (!formik.values.authToken?.trim()) {
      await Promise.all([
        formik.setFieldTouched("authToken", true, false),
        formik.setFieldError("authToken", "Auth Token is required"),
      ]);
      return;
    }

    formik.setFieldError("authToken", undefined);
    try {
      await dispatch(
        upsertIntegrationSetting({
          uuid: setting?.uuid,
          integrationId,
          accessToken: formik.values.authToken,
          isInstallationCompleted: true,
        }),
      ).unwrap();

      setIsEditingAuthToken(false);
      await fetchUpdatedSetting();

      toast.success("Auth Token updated successfully");
    } catch (error) {
      console.error(error);
      toast.error(error?.message || "Failed to update Auth Token");
    }
  };

  return (
    <div className="container my-6">
      <div
        className="card border-0 shadow-lg"
        style={{
          marginTop: "60px",
        }}
      >
        <div className="card-body p-4 p-md-5">
          <div className="d-flex justify-content-between align-items-start mb-5">
            <div className="d-flex align-items-center">
              <div
                className="rounded-circle bg-gradient d-flex justify-content-center align-items-center me-3 flex-shrink-0"
                style={{
                  width: "60px",
                  height: "60px",
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  boxShadow: "0 4px 15px rgba(102,126,234,0.4)",
                }}
              >
                {iconSrc && (
                  <img
                    src={iconSrc}
                    alt={integrationName}
                    style={{
                      width: "36px",
                      height: "36px",
                    }}
                  />
                )}
              </div>

              <div>
                <div className="flex-grow-1">
                  {!isEditingName ? (
                    <div className="d-flex align-items-center">
                      <h3 className="mb-1 fw-bold text-truncate">
                        {formatIntegrationName(
                          setting?.integrationName || integrationName,
                        )}
                      </h3>

                      <button
                        className="btn btn-sm flex-shrink-0"
                        onClick={() => {
                          setIsEditingName(true);
                          setEditedName(
                            getEditableIntegrationName(
                              setting?.integrationName || integrationName,
                            ),
                          );
                        }}
                        aria-label="Edit integration name"
                      >
                        <i className={`wb ${Icons("edit")}`} />
                      </button>
                    </div>
                  ) : (
                    <div className="d-flex align-items-center gap-2 flex-grow-1">
                      <input
                        type="text"
                        className="form-control bg-opacity-25 border-secondary"
                        value={editedName}
                        onChange={(e) => setEditedName(e.target.value)}
                      />

                      <button
                        className="btn btn-sm btn-success"
                        onClick={async () => {
                          dispatch(
                            upsertIntegrationSetting({
                              uuid: setting?.uuid,
                              integrationName: toIntegrationNameKey(editedName),
                            }),
                          );

                          setIsEditingName(false);
                          fetchUpdatedSetting();
                        }}
                      >
                        <i className={`wb ${Icons("correct")}`} />
                      </button>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => setIsEditingName(false)}
                      >
                        <i className={`wb ${Icons("cancel")}`} />
                      </button>
                    </div>
                  )}

                  <p
                    className="text-muted mb-0"
                    style={{
                      fontSize: "0.9rem",
                    }}
                  >
                    Manage your integration settings and conversations
                  </p>
                </div>
              </div>
            </div>
            <div className="d-flex align-items-center gap-1">
              <button
                type="button"
                className="btn btn-sm btn-setup d-flex align-items-center gap-2"
                onClick={() => handleConfiguration(setting)}
              >
                <i className={`wb ${Icons("constraints")}`} />
                Settings
              </button>
            </div>
          </div>

          {isLoading ? (
            <LoadingComponent loading={true} size={15} />
          ) : (
            <>
              {!selectedIntegration?.isOauthRequired && (
                <div
                  className="card border shadow-sm mb-4"
                  style={{
                    borderRadius: "14px",
                  }}
                >
                  <div className="card-body p-4">
                    <div className="d-flex justify-content-between align-items-start mb-4">
                      <div>
                        <h5 className="fw-bold mb-1">
                          Account SID and Auth Token
                        </h5>

                        <p
                          className="text-muted mb-0"
                          style={{
                            fontSize: "0.9rem",
                          }}
                        >
                          View and update your Account SID and Auth Token
                        </p>
                      </div>

                      <span className="badge bg-success-subtle text-success px-3 py-2">
                        Connected
                      </span>
                    </div>

                    <form onSubmit={formik.handleSubmit}>
                      <label
                        className="text-muted d-block mb-2"
                        style={{
                          fontSize: "0.85rem",
                        }}
                      >
                        Account SID
                      </label>

                      <div
                        className="d-flex align-items-center justify-content-between px-3"
                        style={{
                          minHeight: "52px",
                          borderRadius: "10px",
                          border:
                            formik.touched.accountSID &&
                            formik.errors.accountSID
                              ? "1px solid rgba(241,21,21,0.47)"
                              : "1px solid rgba(255,255,255,0.15)",
                          background: "rgba(255,255,255,0.03)",
                        }}
                      >
                        <div className="d-flex align-items-center flex-grow-1">
                          <i className="wb webconnect-file-person me-2 text-muted" />

                          {isEditingAccountSID ? (
                            <input
                              type="text"
                              name="accountSID"
                              className="form-control border-0 bg-transparent shadow-none px-2"
                              placeholder="Enter Account SID"
                              value={formik.values.accountSID}
                              onChange={(e) => {
                                formik.handleChange(e);
                              }}
                              onBlur={formik.handleBlur}
                            />
                          ) : (
                            <span className="px-2">
                              {setting?.connectionIds?.id1 || "-"}
                            </span>
                          )}
                        </div>

                        <div className="d-flex align-items-center gap-3">
                          {!isEditingAccountSID && (
                            <>
                              <button
                                type="button"
                                className="btn btn-sm btn-primary"
                                onClick={() => setIsEditingAccountSID(true)}
                              >
                                Edit
                              </button>
                            </>
                          )}

                          {isEditingAccountSID && (
                            <div className="d-flex align-items-center gap-2">
                              <button
                                type="button"
                                className="btn btn-sm btn-primary"
                                disabled={settingLoading}
                                onClick={handleAccountSIDSave}
                              >
                                Save
                              </button>

                              <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary"
                                onClick={() => {
                                  setIsEditingAccountSID(false);

                                  formik.resetForm();
                                }}
                              >
                                Cancel
                              </button>
                            </div>
                          )}
                        </div>
                      </div>

                      {formik.touched.accountSID &&
                        formik.errors.accountSID && (
                          <div
                            className="text-danger mt-2"
                            style={{
                              fontSize: "0.8rem",
                            }}
                          >
                            {formik.errors.accountSID}
                          </div>
                        )}

                      <label
                        className="text-muted d-block mb-2 mt-4"
                        style={{
                          fontSize: "0.85rem",
                        }}
                      >
                        Auth Token
                      </label>

                      <div
                        className="d-flex align-items-center justify-content-between px-3"
                        style={{
                          minHeight: "52px",
                          borderRadius: "10px",
                          border:
                            formik.touched.authToken && formik.errors.authToken
                              ? "1px solid rgba(241,21,21,0.47)"
                              : "1px solid rgba(255,255,255,0.15)",
                          background: "rgba(255,255,255,0.03)",
                        }}
                      >
                        <div className="d-flex align-items-center flex-grow-1">
                          <i className="wb webconnect-key me-2 text-muted" />

                          {isEditingAuthToken ? (
                            <>
                              <input
                                type="text"
                                name="authToken"
                                className="form-control border-0 bg-transparent shadow-none px-2"
                                placeholder="Enter Auth Token"
                                value={formik.values.authToken}
                                onChange={(e) => {
                                  formik.handleChange(e);
                                }}
                                onBlur={formik.handleBlur}
                              />
                            </>
                          ) : (
                            <span className="px-2">
                              {setting?.maskedAccessToken || "-"}
                            </span>
                          )}
                        </div>

                        <div className="d-flex align-items-center gap-3">
                          {!isEditingAuthToken && (
                            <>
                              <button
                                type="button"
                                className="btn btn-sm btn-primary"
                                onClick={() => setIsEditingAuthToken(true)}
                              >
                                Edit
                              </button>
                            </>
                          )}

                          {isEditingAuthToken && (
                            <div className="d-flex align-items-center gap-2">
                              <button
                                type="button"
                                className="btn btn-sm btn-primary"
                                disabled={settingLoading}
                                onClick={handleAuthTokenSave}
                              >
                                Save
                              </button>

                              <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary"
                                onClick={() => {
                                  setIsEditingAuthToken(false);

                                  formik.resetForm();
                                }}
                              >
                                Cancel
                              </button>
                            </div>
                          )}
                        </div>
                      </div>

                      {formik.touched.authToken && formik.errors.authToken && (
                        <div
                          className="text-danger mt-2"
                          style={{
                            fontSize: "0.8rem",
                          }}
                        >
                          {formik.errors.authToken}
                        </div>
                      )}
                    </form>
                  </div>
                </div>
              )}

              <div
                className="card border shadow-sm"
                style={{
                  borderRadius: "14px",
                }}
              >
                <div className="card-body p-4">
                  <div className="mb-4">
                    <h5 className="fw-bold mb-1">Manage Your Integration</h5>

                    <p
                      className="text-muted mb-0"
                      style={{
                        fontSize: "0.9rem",
                      }}
                    >
                      Delete your integration settings and conversations
                    </p>
                  </div>
                  <div className="mt-4 d-flex justify-content-end">
                    <button
                      className="btn btn-danger"
                      onClick={handleDelete}
                      disabled={settingLoading}
                    >
                      Delete Integration
                    </button>
                  </div>
                </div>
              </div>

              <div className="mt-4">
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => navigate("/integrations")}
                >
                  Back
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default IntegrationDetails;
